import { OperationLabel } from '@core/models/operation-label.model';
import { JobFile } from '@core/models/job-file/job-file.model';
import { ServiceSequenceTemplate } from '../../../wizard/models/service-sequence-template.model';
import { AddContainer } from '../../../wizard/models/add-container.model';
import { EntityMap } from '@shared/models/types';
import { VehicleCategory } from '@shared/models/vehicle-category.model';
import { PartnerOrganization, PartnerOrganizationSearch } from '../partner-organization.model';
import { PartnerVehicle, DigitalTwinVehicle } from '../partner-vehicle.model';
import { WorkOrderDefinition } from '@control-tower/models/work-order/work-order-definition.model';
import { WorkFlowDefinition } from '../../../wizard/models/work-flow-definition';
import { WaypointData } from '../../../wizard/models/waypoint-data.model';
import { JobFileContainer } from '@core/models/job-file';
import { JobFileServiceModel } from '@core/models/job-file/job-file-service.model';

/**
 * A service returned by a successful AddService POST. The add response is the only place the
 * server-generated service id is available immediately; without it the dialog has to wait for a full
 * LoadJobFile refetch before it knows a row was saved, and on a slow connection a second Confirm in
 * that window re-adds every row it has not yet matched to an id.
 */
export interface AddedService {
  containerId: string;
  index: number;
  service: JobFileServiceModel;
}

/**
 * Key for `addedServices`. Adds are identified by the container and the insert index they were sent
 * with, which is unique per row, so concurrent adds cannot be confused with one another - matching
 * on activity/location alone would let two rows with the same activity at the same location swap ids.
 */
export const addedServiceKey = (containerId: string, index: number): string => `${containerId}::${index}`;

export interface LoadManagementStateModel {
  jobFile: JobFile | null;
  loading: boolean;
  error: string | null;
  savedRoutes: ServiceSequenceTemplate[];
  containers: EntityMap<string, AddContainer>;
  vehicleCategories: VehicleCategory[];
  standardVehicleCategories: VehicleCategory[];
  partnerOrganizations: PartnerOrganization[];
  partnerOrganizationsSearch: PartnerOrganizationSearch[];
  vehiclesByOrganization: EntityMap<string, PartnerVehicle[]>;
  loadingPartnerOrgs: boolean;
  loadingVehicles: boolean;
  digitalTwinVehicles: DigitalTwinVehicle[];
  digitalTwinLoading: boolean;
  digitalTwinPage: number;
  digitalTwinTotalPages: number;
  digitalTwinPageSize: number;
  activityList: WorkOrderDefinition[];
  workflowDefinition: WorkFlowDefinition | null;
  undoWaypoints: EntityMap<string, WaypointData[]>;
  redoWaypoints: EntityMap<string, WaypointData[]>;
  rootLoads: JobFileContainer[];
  addedServices: Record<string, AddedService>;
  /** PB-5281: labels (active and inactive) of the job's operation. */
  operationLabels: OperationLabel[];
  operationLabelsOperationId: string | null;
  /** PB-5281: loads of the job that are on an invoice; their labels are read-only. */
  invoicedLoadIds: string[];
}

